import { CognitoUserPool, CognitoUser, CognitoUserAttribute, AuthenticationDetails, } from 'amazon-cognito-identity-js';
export const defaultMessageCallBack = (error, result) => {
    if (error && (error === null || error === void 0 ? void 0 : error.message))
        return error.message;
    else if (result)
        return result;
    else
        return 'No error nor result received.';
};
export class CognitoAuth {
    constructor(awsConfig) {
        this.LatestUsedUsername = '';
        this.IsAuthenticated = false;
        this.UserData = undefined;
        this.PoolData = awsConfig;
        this.UserPool = new CognitoUserPool(this.PoolData);
    }
    /**
     * Creates a new CognitoUser object.
     * @param username User's username.
     * @returns Cognito User
     */
    GetCognitoUser(username) {
        if (!this.CognitoUser || this.CognitoUser.getUsername() !== username) {
            this.CognitoUser = new CognitoUser({
                Username: username,
                Pool: this.UserPool,
            });
        }
    }
    /**
     * Sets the latest username to use it in further operations.
     * @param username
     */
    SetLatestUsedUsername(username) {
        this.LatestUsedUsername = username;
    }
    /**
     * Function to handle callback's responses from Amazon and return them.
     * @param {Message}
     * @param mode
     */
    DisplayMessage({ Status, Message }, mode) {
        const message = { Status, Message };
        let messageUI;
        let messageUIError;
        try {
            messageUI = document.getElementById('cognito-auth-messages');
        }
        catch (error) {
            messageUIError = error;
            messageUI = null;
        }
        if (mode === 'console')
            console.log(message);
        else if (mode === 'ui' && messageUI) {
            messageUI.innerHTML = `<p>${Status}: ${message.Message}</p>`;
        }
        else if (mode === 'ui' && !messageUI) {
            console.log('Did not found "#cognito-auth-messages" element:\n', messageUIError, '\nDisplaying message on console instead:\n', message);
        }
    }
    /**
     * Transforms a list of attributes to use it with Cognito.
     * @param attributes Cognito Pool's list of user attributes.
     * @returns Cognito Attributes List.
     */
    GetAttributesList(attributes) {
        const cognitoAttributes = [];
        for (let attr of attributes) {
            let cognitoAttr = new CognitoUserAttribute(attr);
            cognitoAttributes.push(cognitoAttr);
        }
        return cognitoAttributes;
    }
    // Public Properties & Methods
    /**
     *
     * @param username User's username.
     * @param password Minimum length of 8 characters. At least 1 special character, 1 number, 1 uppercase letter and 1 lowercase letter.
     * @param attributeList Email, phone, addresses, etc.
     * @param messageMode How you want to display the message.
     * @param messageCallBack Callback function that provided an error or result returns a message (string).
     * @returns An error or the register result.
     */
    SignUp(username, password, attributeList, messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        const cognitoAttributeList = this.GetAttributesList(attributeList);
        this.UserPool.signUp(username, password, cognitoAttributeList, [], (error, result) => {
            if (error) {
                this.DisplayMessage({
                    Status: 'error',
                    Message: messageCallBack(error, result) || error.message,
                }, messageMode);
            }
            else if (result) {
                this.DisplayMessage({
                    Status: 'success',
                    Message: messageCallBack(undefined, result) ||
                        'Sign Up successful!',
                }, messageMode);
            }
            else {
                this.DisplayMessage({
                    Status: 'error',
                    Message: 'Neither an error nor a result was provided.',
                }, messageMode);
            }
        });
        this.SetLatestUsedUsername(username);
        this.GetCognitoUser(username);
    }
    /**
     * Confirms user sign up on Cognito server.
     * @param confirmationCode Confirmation Code sent by AWS Cognito.
     * @param messageMode How you want to display the message.
     * @param messageCallBack Callback function that provided an error or result returns a message (string).
     * @returns An error or the confirmation result.
     */
    ConfirmSignUp(confirmationCode, messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        var _a;
        this.GetCognitoUser(this.LatestUsedUsername);
        (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.confirmRegistration(confirmationCode, false, (error, result) => {
            if (error) {
                this.DisplayMessage({
                    Status: 'error',
                    Message: messageCallBack(error),
                }, messageMode);
            }
            else if (result) {
                this.DisplayMessage({
                    Status: 'success',
                    Message: messageCallBack(undefined, result),
                }, messageMode);
            }
            else {
                this.DisplayMessage({
                    Status: 'error',
                    Message: 'Neither an error nor a result was provided.',
                }, messageMode);
            }
        });
    }
    /**
     * Resends a confirmation code to the registering user's email.
     * @param messageMode How you want to display the message.
     * @param messageCallBack Callback function that provided an error or result returns a message (string).
     * @returns An error or the resend confirmation code result.
     */
    ResendConfirmationCode(messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        var _a;
        this.GetCognitoUser(this.LatestUsedUsername);
        if (!this.CognitoUser) {
            this.DisplayMessage({
                Status: 'error',
                Message: 'No Cognito User provided!',
            }, messageMode);
        }
        else {
            (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.resendConfirmationCode((error, result) => {
                if (error) {
                    this.DisplayMessage({
                        Status: 'error',
                        Message: messageCallBack(error),
                    }, messageMode);
                }
                else if (result) {
                    this.DisplayMessage({
                        Status: 'success',
                        Message: messageCallBack(undefined, result),
                    }, messageMode);
                }
                else {
                    this.DisplayMessage({
                        Status: 'error',
                        Message: 'Neither an error nor a result was provided.',
                    }, messageMode);
                }
            });
        }
    }
    /**
     * Signs the user into Cognito's pool and into the application.
     * @param username
     * @param password
     * @param messageMode How you want to display the message.
     * @param messageCallBack Callback function that provided an error or result returns a message (string).
     */
    SignIn(username, password, messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        var _a;
        const authDetails = new AuthenticationDetails({
            Username: username,
            Password: password,
        });
        this.SetLatestUsedUsername(username);
        this.GetCognitoUser(username);
        (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.authenticateUser(authDetails, {
            onSuccess: (result) => {
                var _a;
                this.IsAuthenticated = true;
                (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.getUserData((error, data) => {
                    if (error) {
                        console.error(error);
                        return;
                    }
                    this.UserData = data;
                });
                this.DisplayMessage({
                    Status: 'success',
                    Message: messageCallBack(undefined, result),
                }, messageMode);
            },
            onFailure: (error) => {
                this.DisplayMessage({
                    Status: 'error',
                    Message: messageCallBack(error),
                }, messageMode);
            },
        });
    }
    /**
     * Signs the user into Cognito's pool and into the application using either the session object
     * or from  the local storage, or by using a refresh token.
     * @param messageMode
     * @param messageCallBack
     */
    CachedSignIn(messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        if (!this.CognitoUser) {
            this.CognitoUser = this.UserPool.getCurrentUser() || undefined;
        }
        if (this.CognitoUser) {
            // Sign in Cognito user
            this.CognitoUser.getSession((error, session) => {
                var _a;
                if (error) {
                    this.DisplayMessage({
                        Status: 'error',
                        Message: messageCallBack(error),
                    }, messageMode);
                }
                else if (session) {
                    this.DisplayMessage({
                        Status: 'success',
                        Message: messageCallBack(undefined, session),
                    }, messageMode);
                    (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.getUserData((error, data) => {
                        if (error) {
                            console.error(error);
                            return;
                        }
                        this.UserData = data;
                    });
                    this.IsAuthenticated = true;
                }
            });
        }
    }
    /**
     * Changes the authenticated user's password.
     * @param username
     * @param oldPassword
     * @param newPassword
     * @param messageMode How you want to display the message.
     * @param messageCallBack Callback function that provided an error or result returns a message (string).
     */
    ChangePassword(username, oldPassword, newPassword, messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        var _a;
        this.SetLatestUsedUsername(username);
        this.GetCognitoUser(username);
        (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.changePassword(oldPassword, newPassword, (error, result) => {
            if (error) {
                this.DisplayMessage({
                    Status: 'error',
                    Message: messageCallBack(error),
                }, messageMode);
            }
            else if (result) {
                this.DisplayMessage({
                    Status: 'success',
                    Message: messageCallBack(undefined, result),
                }, messageMode);
            }
            else {
                this.DisplayMessage({
                    Status: 'error',
                    Message: 'Neither an error nor a result was provided.',
                }, messageMode);
            }
        });
    }
    /**
     * Starts the forgotten password auth flow: Users'll receive a confirmation code to change their password.
     * @param username
     * @param messageMode
     * @param messageCallBack
     */
    ForgotPassword(username, messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        var _a;
        this.SetLatestUsedUsername(username);
        this.GetCognitoUser(username);
        (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.forgotPassword({
            onSuccess: (result) => {
                this.DisplayMessage({
                    Status: 'success',
                    Message: messageCallBack(undefined, result),
                }, messageMode);
            },
            onFailure: (error) => {
                this.DisplayMessage({
                    Status: 'error',
                    Message: messageCallBack(error),
                }, messageMode);
            },
        });
    }
    /**
     * Verifies the forgotten password auth flow and allows users to change their password.
     * @param username
     * @param verificationCode
     * @param newPassword
     * @param messageMode
     * @param messageCallBack
     */
    ForgotPasswordVerification(username, verificationCode, newPassword, messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        var _a;
        this.SetLatestUsedUsername(username);
        this.GetCognitoUser(username);
        (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.confirmPassword(verificationCode, newPassword, {
            onSuccess: (result) => {
                this.DisplayMessage({
                    Status: 'success',
                    Message: messageCallBack(undefined, result),
                }, messageMode);
            },
            onFailure: (error) => {
                this.DisplayMessage({
                    Status: 'error',
                    Message: messageCallBack(error),
                }, messageMode);
            },
        });
    }
    /**
     * Signs out the specified user.
     * @param invalidateTokens Automatically invalidates Cognito's Tokens on Local Storage to avoid cached login after a sign out.
     * @param messageMode How you want to display the message.
     * @param messageCallBack Callback function that provided an error or result returns a message (string).
     */
    SignOut(invalidateTokens, messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        var _a, _b, _c, _d, _e, _f, _g;
        this.GetCognitoUser(this.LatestUsedUsername);
        if (invalidateTokens) {
            // Globally Sign Out by invalidating tokens
            (_a = this.CognitoUser) === null || _a === void 0 ? void 0 : _a.globalSignOut({
                onSuccess: (msg) => {
                    this.DisplayMessage({
                        Status: 'success',
                        Message: messageCallBack(undefined, 'Successfully signed out.'),
                    }, messageMode);
                },
                onFailure: (error) => {
                    this.DisplayMessage({
                        Status: 'error',
                        Message: messageCallBack(error),
                    }, messageMode);
                },
            });
            let tokens = [
                `CognitoIdentityServiceProvider.${this.PoolData.ClientId}.${(_b = this.UserData) === null || _b === void 0 ? void 0 : _b.Username}.accessToken`,
                `CognitoIdentityServiceProvider.${this.PoolData.ClientId}.${(_c = this.UserData) === null || _c === void 0 ? void 0 : _c.Username}.clockDrift`,
                `CognitoIdentityServiceProvider.${this.PoolData.ClientId}.${(_d = this.UserData) === null || _d === void 0 ? void 0 : _d.Username}.idToken`,
                `CognitoIdentityServiceProvider.${this.PoolData.ClientId}.${(_e = this.UserData) === null || _e === void 0 ? void 0 : _e.Username}.refreshToken`,
                `CognitoIdentityServiceProvider.${this.PoolData.ClientId}.${(_f = this.UserData) === null || _f === void 0 ? void 0 : _f.Username}.userData`,
                `CognitoIdentityServiceProvider.${this.PoolData.ClientId}.LastAuthUser`,
            ];
            for (let token of tokens) {
                localStorage.removeItem(token);
            }
        }
        else {
            (_g = this.CognitoUser) === null || _g === void 0 ? void 0 : _g.signOut(() => this.DisplayMessage({
                Status: 'success',
                Message: messageCallBack(undefined, 'Successfully signed out.'),
            }, messageMode));
        }
        this.IsAuthenticated = false;
    }
    /**
     * Gets the authenticated user's data.
     * @returns User Data, including the user's attributes and username, or false in case there is no authenticated user. Might return an error if user is authenticated but couldn't retrieve data.
     */
    GetUserData(messageMode = 'none', messageCallBack = defaultMessageCallBack) {
        if (!this.IsAuthenticated) {
            const error = new Error('User is not authenticated');
            this.DisplayMessage({
                Status: 'error',
                Message: messageCallBack(error),
            }, messageMode);
            return false;
        }
        else if (this.UserData !== undefined) {
            this.DisplayMessage({
                Status: 'success',
                Message: messageCallBack(undefined, 'Successfully got user data.'),
            }, messageMode);
            return this.UserData;
        }
        else {
            const error = new Error("User is authenticated but we couldn't get the correspondent data.");
            this.DisplayMessage({
                Status: 'error',
                Message: messageCallBack(error),
            }, messageMode);
            throw error;
        }
    }
}
//# sourceMappingURL=index.js.map